import React from "react";
import { Chips } from "primereact/chips";
import FieldDropdown from "../common/InputField/FieldDropdown";
import {
  AdminDetailFieldLabel,
  AdminFormFieldError,
  AdminSupportingText,
  AdminMetadataText,
  AdminMainText,
  AdminHeaderAction,
  AdminFormFieldLabelRow,
  adminWizardDropdownClasses,
  adminWizardInputClasses,
  adminWizardTextareaClasses,
  adminWizardChipsClasses,
  adminWizardUploadClasses,
} from "../../sections/AdminDashboard/shared/adminUx";

export const ACCOUNT_POPUP_MUTED_PANEL = "custom-dropdown-panel !bg-mistygray";

/**
 * Standard wizard field for account setting popups (352px).
 * Supports text, textarea, and dropdown step types.
 */
export function AccountPopupField({
  step,
  value,
  onChange,
  form,
  fieldKey,
}) {
  const hasError = Boolean(form?.touched?.[fieldKey] && form?.errors?.[fieldKey]);
  const errorMsg = hasError ? form.errors[fieldKey] : null;

  if (step.type === "dropdown") {
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>{step.title}</AdminDetailFieldLabel>
        {step.description ? (
          <AdminSupportingText className="text-mediumgray mb-2 block">{step.description}</AdminSupportingText>
        ) : null}
        <FieldDropdown
          surface="muted"
          fullWidth
          value={value ?? ""}
          onChange={(e) => onChange(e.value)}
          options={step.options}
          optionLabel={step.optionLabel || "label"}
          optionValue={step.optionValue || "value"}
          placeholder={step.placeholder || `Select ${String(step.title).toLowerCase()}`}
          className={adminWizardDropdownClasses(hasError, true)}
          panelClassName={ACCOUNT_POPUP_MUTED_PANEL}
          appendTo={null}
          showClear={false}
        />
        {errorMsg ? <AdminFormFieldError>{errorMsg}</AdminFormFieldError> : null}
      </div>
    );
  }

  if (step.type === "textarea") {
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel htmlFor={fieldKey}>{step.title}</AdminDetailFieldLabel>
        {step.description ? (
          <AdminSupportingText className="text-mediumgray mb-2 block">{step.description}</AdminSupportingText>
        ) : null}
        <textarea
          id={fieldKey}
          name={fieldKey}
          placeholder={step.placeholder}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={step.rows || 4}
          className={adminWizardTextareaClasses(hasError)}
          style={{ resize: "none" }}
        />
        {errorMsg ? <AdminFormFieldError>{errorMsg}</AdminFormFieldError> : null}
      </div>
    );
  }

  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel htmlFor={fieldKey}>{step.title}</AdminDetailFieldLabel>
      {step.description ? (
        <AdminSupportingText className="text-mediumgray mb-2 block">{step.description}</AdminSupportingText>
      ) : null}
      <input
        id={fieldKey}
        type={step.inputType || step.type || "text"}
        name={fieldKey}
        placeholder={step.placeholder}
        value={value ?? ""}
        readOnly={step.readOnly}
        disabled={step.readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={`${adminWizardInputClasses(hasError)} ${step.readOnly ? "bg-mistygray text-mediumgray cursor-not-allowed" : ""} ${step.inputClassName || ""}`.trim()}
      />
      {errorMsg ? <AdminFormFieldError>{errorMsg}</AdminFormFieldError> : null}
    </div>
  );
}

export function AccountPopupSaveFooter({ onClick, label = "Save", disabled = false }) {
  return (
    <div className="flex items-center justify-end mt-10 w-full">
      <AdminHeaderAction type="button" onClick={onClick} disabled={disabled}>
        {label}
      </AdminHeaderAction>
    </div>
  );
}

export function AccountPopupStepFooter({
  onBack,
  onNext,
  nextLabel = "Next",
  showBack = true,
  nextClassName = "",
  nextDisabled = false,
}) {
  return (
    <div className="flex items-center justify-between mt-10 w-full">
      {showBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-primaryblue hover:text-brightblue dashboard-text-main"
        >
          Back
        </button>
      ) : (
        <span />
      )}
      <AdminHeaderAction
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        className={nextClassName}
      >
        {nextLabel}
      </AdminHeaderAction>
    </div>
  );
}

/** PrimeReact chips field (service areas, tags). */
export function AccountPopupChipsField({
  title,
  tooltip,
  description,
  value = [],
  onChange,
  placeholder,
  max = 10,
  form,
  fieldKey,
}) {
  const hasError = Boolean(form?.touched?.[fieldKey] && form?.errors?.[fieldKey]);
  const errorMsg = hasError ? form.errors[fieldKey] : null;
  const chips = Array.isArray(value) ? value : [];

  return (
    <div className="space-y-1 w-full min-w-0">
      {tooltip ? (
        <AdminFormFieldLabelRow label={title} tooltip={description || tooltip} />
      ) : (
        <AdminDetailFieldLabel htmlFor={fieldKey}>{title}</AdminDetailFieldLabel>
      )}
      {description && !tooltip ? (
        <AdminSupportingText className="text-mediumgray mb-2 block">{description}</AdminSupportingText>
      ) : null}
      <Chips
        id={fieldKey}
        inputId={fieldKey}
        placeholder={placeholder}
        className={`${adminWizardChipsClasses(chips.length > 0, hasError)} placeholder-lightgray text-offblack`}
        value={chips}
        onChange={(e) => onChange((e.value || []).slice(0, max))}
        separator=","
      />
      {errorMsg ? <AdminFormFieldError>{errorMsg}</AdminFormFieldError> : null}
    </div>
  );
}

/** Dashboard upload drop target — Upload Area preset, full wizard width. */
export function AccountPopupUploadArea({ htmlFor, children, className = "" }) {
  return (
    <label
      htmlFor={htmlFor}
      role="button"
      className={`${adminWizardUploadClasses()} hover:bg-mistygray transition-colors ${className}`.trim()}
    >
      {children}
    </label>
  );
}

export function AccountPopupUploadPlaceholder({
  iconClass = "fi fi-rs-inbox-out",
  label = "Click to upload",
  hint,
}) {
  return (
    <div className="my-auto">
      <div className="flex justify-center">
        <i className={`${iconClass} text-base text-offblack`} aria-hidden />
      </div>
      <div className="flex justify-center">
        <AdminSupportingText className="mt-1 text-primaryblue block text-center">
          {label}
        </AdminSupportingText>
      </div>
      {hint ? (
        <div className="flex justify-center">
          <AdminMetadataText className="text-mediumgray mt-2 block text-center">
            {hint}
          </AdminMetadataText>
        </div>
      ) : null}
    </div>
  );
}

/** Checkbox list for payment methods, specialities, etc. */
export function AccountPopupCheckboxGroup({
  title,
  tooltip,
  description,
  options,
  selectedValues = [],
  onToggle,
  getValue = (opt) => opt.value ?? opt.id,
  getLabel = (opt) => opt.label ?? opt.name,
  max,
}) {
  const selected = Array.isArray(selectedValues) ? selectedValues : [];

  return (
    <div className="w-full min-w-0">
      {tooltip ? (
        <AdminFormFieldLabelRow label={title} tooltip={tooltip} />
      ) : (
        <AdminDetailFieldLabel className="mb-2 block">{title}</AdminDetailFieldLabel>
      )}
      {description ? (
        <AdminSupportingText className="text-mediumgray mb-4 block">{description}</AdminSupportingText>
      ) : null}
      <div className="space-y-2 max-h-[320px] overflow-y-auto">
        {options.map((opt) => {
          const val = getValue(opt);
          const checked = selected.includes(val);
          const atMax = max != null && selected.length >= max && !checked;
          return (
            <label
              key={val}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg border border-mediumgray cursor-pointer hover:border-primaryblue ${
                checked ? "bg-pastalblue border-primaryblue" : "bg-white"
              } ${atMax ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <input
                type="checkbox"
                className="size-4 rounded border-mediumgray text-primaryblue focus:ring-primaryblue"
                checked={checked}
                disabled={atMax}
                onChange={(e) => onToggle(val, e.target.checked)}
              />
              <AdminMainText className="text-offblack">{getLabel(opt)}</AdminMainText>
            </label>
          );
        })}
      </div>
    </div>
  );
}
